<template>
  <div
    class="bank-links"
    :class="{ 'bank-links--verification': isVerificationFlowActive }"
    :aria-busy="isBusy || isLoading ? 'true' : 'false'"
  >
    <div class="bank-links__content">
      <alert-box class="bank-links__alert" level="warning" v-if="errorMessage">
        <span>{{ errorMessage }}</span>
        <template v-if="errorSupport === 'aerosync'">
          <span> Please try again later or contact </span>
          <a :href="providerSupportUrl" rel="noopener noreferrer" v-outer-link>Aerosync Support</a>
          <span> for assistance.</span>
        </template>
        <template v-else-if="errorSupport === 'predictit'">
          <span> If you believe this is an error, contact </span>
          <router-link to="/support" rel="noopener noreferrer" v-outer-link
            >PredictIt Support</router-link
          ><span>.</span>
        </template>
      </alert-box>

      <alert-box class="bank-links__alert" level="info" v-if="advisoryMessage">
        <span>{{ advisoryMessage }}</span>
        <template v-if="advisoryActionUrl">
          <span> Contact </span>
          <a :href="advisoryActionUrl" rel="noopener noreferrer" v-outer-link>Aerosync Support</a>
        </template>
      </alert-box>

      <alert-box
        class="bank-links__alert bank-links__alert--progress"
        level="info"
        v-if="isCompletingLink"
      >
        {{ completionProgressText }}
      </alert-box>

      <div class="bank-links__content-header">
        <div class="bank-links__content-header-left" v-if="isManagingAccounts">
          <button
            class="bank-links__content-header-action bank-links__content-header-action--back"
            type="button"
            @click="handleManageBack"
          >
            <span
              class="bank-links__content-header-icon bank-links__content-header-icon--back"
            ></span>
            <span class="bank-links__content-header-label">BACK</span>
          </button>
          <div class="bank-links__content-header-title">Delete Account</div>
        </div>
        <div class="bank-links__content-header-title" v-else>
          {{ contentTitle }}
        </div>
        <button
          class="bank-links__content-header-action"
          type="button"
          v-if="
            !isPhoneVerificationRequired &&
            !isProviderConfirmationReady &&
            !isProviderConfirmationRequired &&
            !isManagingAccounts &&
            accounts.length > 0 &&
            !isBusy
          "
          @click="enterManageAccounts"
        >
          <span class="bank-links__content-header-label">MANAGE</span>
          <span
            class="bank-links__content-header-icon bank-links__content-header-icon--edit"
          ></span>
        </button>
      </div>

      <div class="bank-links__verification-stage" v-if="isVerificationFlowActive">
        <div class="bank-links__verification-header">
          <span class="bank-links__verification-icon" aria-hidden="true">
            <svg
              v-if="isPhoneVerificationRequired"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <rect x="6.5" y="2.5" width="11" height="19" rx="2.5" />
              <path d="M10.5 18h3" />
            </svg>
            <svg
              v-else
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            >
              <path d="M12 2.8 4.5 5.6v5.6c0 4.6 3.2 8.5 7.5 10 4.3-1.5 7.5-5.4 7.5-10V5.6z" />
              <path d="m8.8 12.1 2.2 2.2 4.3-4.4" />
            </svg>
          </span>
          <p class="bank-links__verification-step" v-if="verificationStepLabel">
            {{ verificationStepLabel }}
          </p>
          <h3 class="bank-links__verification-title">{{ verificationTitle }}</h3>
        </div>
        <template v-if="isPhoneVerificationRequired">
          <div
            class="bank-links__phone-required"
            aria-live="polite"
            v-if="!isPhoneVerificationVisible"
          >
            <p class="bank-links__phone-required-message">
              {{ phoneRequirementMessage }}
            </p>
            <button
              class="button button--primary button--full-width"
              type="button"
              ref="addPhoneNumberButton"
              @click="showPhoneVerification"
            >
              {{ phoneRequirementActionText }}
            </button>
            <button
              class="bank-links__verification-action"
              type="button"
              @click="handlePhoneVerificationCancel"
            >
              Cancel
            </button>
          </div>
          <account-phone-update
            bank-link
            v-else
            @close="handlePhoneVerificationCancel"
            @verified="handlePhoneVerified"
          ></account-phone-update>
        </template>

        <div
          class="bank-links__provider-confirmation"
          aria-live="polite"
          v-else-if="isProviderConfirmationReady"
        >
          <p class="bank-links__provider-confirmation-message">
            Aerosync, our bank-linking provider, needs to verify your identity before you can link a
            bank account.
          </p>
          <alert-box class="bank-links__alert" level="warning" v-if="confirmationErrorMessage">
            {{ confirmationErrorMessage }}
          </alert-box>
          <button
            class="button button--primary button--full-width"
            type="button"
            ref="sendProviderCodeButton"
            :disabled="isSendingProviderConfirmation"
            @click="sendProviderVerificationCode(false)"
          >
            {{ isSendingProviderConfirmation ? 'Sending...' : 'Send Verification Code' }}
          </button>
          <button
            class="bank-links__verification-action"
            type="button"
            :disabled="isSendingProviderConfirmation"
            @click="cancelProviderConfirmation"
          >
            Cancel
          </button>
        </div>

        <form
          class="bank-links__provider-confirmation"
          v-else
          @submit.prevent="confirmProviderVerificationCode"
        >
          <p
            class="bank-links__provider-confirmation-message"
            :id="widgetElementId + '-confirmation-code'"
          >
            {{ confirmationPrompt }}
          </p>
          <alert-box class="bank-links__alert" level="warning" v-if="confirmationErrorMessage">
            {{ confirmationErrorMessage }}
          </alert-box>
          <div class="bank-links__provider-confirmation-field">
            <code-input
              ref="providerConfirmationCode"
              role="group"
              :aria-labelledby="widgetElementId + '-confirmation-code'"
              v-model="confirmationCode"
              :error="!!confirmationErrorMessage"
              :disabled="isConfirmingProviderUser || isSendingProviderConfirmation"
              :loading="isConfirmingProviderUser"
              @complete="handleProviderConfirmationComplete"
              @submit="confirmProviderVerificationCode"
            />
          </div>
          <button
            class="button button--primary button--full-width"
            type="submit"
            :disabled="!canSubmitProviderConfirmation"
          >
            {{ isConfirmingProviderUser ? 'Verifying...' : 'Verify and Continue' }}
          </button>
          <div class="bank-links__provider-confirmation-actions">
            <button
              class="bank-links__verification-action"
              type="button"
              :disabled="isSendingProviderConfirmation || confirmationSecondsUntilResend > 0"
              @click="sendProviderVerificationCode(true)"
            >
              {{ isSendingProviderConfirmation ? 'Sending...' : resendButtonText }}
            </button>
            <button
              class="bank-links__verification-action"
              type="button"
              :disabled="isConfirmingProviderUser || isSendingProviderConfirmation"
              @click="cancelProviderConfirmation"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
      <template v-else-if="!isManagingAccounts">
        <no-data-dashed-box v-if="showEmptyState">
          You don't have any bank accounts added
        </no-data-dashed-box>

        <div class="bank-links__accounts" v-if="accounts.length > 0 || showAccountSkeleton">
          <div
            class="bank-links__account"
            :class="{
              'bank-links__account--reconnecting': accountIsReconnecting(account),
              'bank-links__account--selectable': accountIsSelectable(account),
            }"
            :aria-busy="accountIsReconnecting(account) ? 'true' : 'false'"
            :aria-label="
              accountIsSelectable(account) ? 'Select ' + accountDescription(account) : null
            "
            :role="accountIsSelectable(account) ? 'button' : null"
            :tabindex="accountIsSelectable(account) ? 0 : null"
            v-for="account in accounts"
            :key="account.linkedBankAccountId"
            @click="selectAccount(account)"
            @keydown.enter="selectAccount(account)"
            @keydown.space.prevent="selectAccount(account)"
          >
            <img class="bank-links__institution-logo" :src="defaultInstitutionLogo" alt="" />
            <div class="bank-links__account-info">
              <div class="bank-links__institution-name">
                {{ account.institutionName || 'Bank account' }}
              </div>
              <div class="bank-links__account-name">
                {{ accountDescription(account) }}
              </div>
              <div
                class="bank-links__account-status bank-links__account-status--reconnecting"
                aria-live="polite"
                v-if="accountIsReconnecting(account)"
              >
                Aerosync connected. Finishing reconnection...
              </div>
              <div class="bank-links__account-status" v-else-if="accountRequiresReconnect(account)">
                Sign in again to reconnect this account.
              </div>
            </div>
            <div class="bank-links__account-actions" v-if="accountRequiresReconnect(account)">
              <button
                class="button button--white bank-links__reconnect-button"
                type="button"
                :data-linked-bank-account-id="account.linkedBankAccountId"
                :disabled="isBusy"
                @click.stop="addAccount(account)"
              >
                Reconnect
              </button>
            </div>
          </div>

          <div
            class="bank-links__account bank-links__account--skeleton"
            role="status"
            aria-label="Finishing bank account setup"
            v-if="showAccountSkeleton"
          >
            <div class="bank-links__skeleton-logo"></div>
            <div class="bank-links__skeleton-details">
              <div class="bank-links__skeleton-line bank-links__skeleton-line--name"></div>
              <div class="bank-links__skeleton-line bank-links__skeleton-line--account"></div>
            </div>
          </div>
        </div>

        <button
          class="button button--bank-link button--full-width bank-links__add-button"
          type="button"
          ref="addBankAccountButton"
          :disabled="isBusy"
          @click="addAccount()"
        >
          <span v-if="bankLinkButtonText">{{ bankLinkButtonText }}</span>
          <template v-else>
            <span>Add Account with</span>
            <img
              class="bank-links__provider-logo"
              src="~assets/images/logos/aerosync.svg"
              alt="Aerosync"
            />
          </template>
        </button>

        <alert-box
          class="bank-links__alert bank-links__alert--tip"
          level="info"
          v-if="showLinkingTip"
        >
          <template v-slot:title>Tip for Bank Linking Success</template>
          <span>
            Aerosync only links with banks that conform to the highest technical and security
            standards, so some regional banks or credit unions may not be able to complete the link
            process. If you experience this, we recommend trying a national bank or contacting
          </span>
          <a :href="providerSupportUrl" rel="noopener noreferrer" v-outer-link>Aerosync Support</a>
          <span> for detailed assistance.</span>
        </alert-box>

        <p class="bank-links__notice" v-if="featureNotice">
          {{ featureNotice }}
        </p>
      </template>

      <template v-else-if="!accountBeingRemoved">
        <div class="bank-links__accounts">
          <div
            class="bank-links__account"
            v-for="account in accounts"
            :key="account.linkedBankAccountId"
          >
            <img class="bank-links__institution-logo" :src="defaultInstitutionLogo" alt="" />
            <div class="bank-links__account-info">
              <div class="bank-links__institution-name">
                {{ account.institutionName || 'Bank account' }}
              </div>
              <div class="bank-links__account-name">
                {{ accountDescription(account) }}
              </div>
            </div>
            <button
              class="bank-links__remove-icon"
              type="button"
              :aria-label="'Remove ' + (account.institutionName || 'bank account')"
              @click="requestRemoveAccount(account)"
            ></button>
          </div>
        </div>
      </template>

      <template v-else>
        <div class="bank-links__confirm-remove">
          <p class="bank-links__confirm-remove-prompt">
            Are you sure you want to remove this account?
          </p>
          <div class="bank-links__account">
            <img class="bank-links__institution-logo" :src="defaultInstitutionLogo" alt="" />
            <div class="bank-links__account-info">
              <div class="bank-links__institution-name">
                {{ accountBeingRemoved.institutionName || 'Bank account' }}
              </div>
              <div class="bank-links__account-name">
                {{ accountDescription(accountBeingRemoved) }}
              </div>
            </div>
          </div>
          <button
            class="button button--primary button--full-width"
            type="button"
            :disabled="isRemovingAccount"
            @click="confirmRemoveAccount"
          >
            {{ isRemovingAccount ? 'Removing...' : 'Yes' }}
          </button>
        </div>
      </template>
    </div>

    <div class="bank-links__widget" :id="widgetElementId"></div>
  </div>
</template>

<script src="./bank-links.js"></script>
<style src="./bank-links.scss" lang="scss"></style>
